import { Component, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NGXLogger } from 'ngx-logger';
import { BackendErrorType } from 'app/shared/services/errors/backend.errors';
import { MainLayoutComponent } from 'app/shared/components/layouts/main-layout/main-layout.component';
import { BannerComponent } from 'app/shared/components/banner/banner.component';
import { ButtonComponent } from 'app/shared/components/button/button.component';
import { LandingpageConfigurationStateService } from 'app/shared/services/state/landingpage-configuration-state.service';
import { StepperComponent } from 'app/shared/components/stepper/stepper.component';
import { AutocompleteValue } from 'app/shared/models/autocomplete-value.model';
import { RequiredFieldsHintComponent } from 'app/shared/components/required-fields-hint/required-fields-hint.component';
import { map } from 'rxjs';

@Component({
  selector: 'ez-authentication',
  imports: [
    ReactiveFormsModule,
    MainLayoutComponent,
    BannerComponent,
    ButtonComponent,
    StepperComponent,
    RequiredFieldsHintComponent,
  ],
  templateUrl: './authentication.component.html',
  styleUrl: './authentication.component.scss',
})
export class AuthenticationComponent {
  protected readonly CONTROL_NAME = 'password';

  private readonly landingpageConfigurationStateService = inject(LandingpageConfigurationStateService);
  private readonly router = inject(Router);
  private readonly logger = inject(NGXLogger);

  readonly organisation = this.landingpageConfigurationStateService.organisation;
  readonly submitAuthenticationTokenRequestIsLoading = signal(false);

  readonly form = new FormGroup({
    [this.CONTROL_NAME]: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
  });

  readonly errorMessage = toSignal(this.authInput.events.pipe(map(() => this.currentErrorMessage())), {
    initialValue: null,
  });

  get authInput(): AbstractControl {
    return this.form.controls[this.CONTROL_NAME];
  }

  private currentErrorMessage(): string | null {
    const authInput = this.authInput;
    if (!authInput.invalid || !(authInput.dirty || authInput.touched)) {
      return null;
    }
    if (authInput.errors?.['required']) {
      return 'Fehler: Dies ist ein Pflichtfeld. Bitte verwenden Sie die letzten vier Ziffern Ihrer IBAN.';
    }
    if (authInput.errors?.[BackendErrorType.VERIFICATION_ERROR] || authInput.errors?.[BackendErrorType.CLIENT_ERROR]) {
      return 'Fehler: Bitte verwenden Sie die letzten vier Ziffern Ihrer IBAN.';
    }
    return 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.';
  }

  async onSubmit() {
    const password = this.form.controls[this.CONTROL_NAME].value;
    this.submitAuthenticationTokenRequestIsLoading.set(true);
    await this.landingpageConfigurationStateService
      .fetchConfigurationForPassword(password)
      .then(() => {
        this.logger.debug('Authentication response:', {
          requestParam: password,
        });

        this.router.navigate(['overview']);
      })
      .catch((error) => {
        if (error.message === BackendErrorType.ALREADY_SENT) {
          this.router.navigate(['already-sent']);
        } else {
          this.authInput.setErrors({ [error.message]: true });
        }
      })
      .finally(() => {
        this.submitAuthenticationTokenRequestIsLoading.set(false);
      });
  }

  protected readonly AutocompleteValue = AutocompleteValue;
}
